/*
 * Copyright 2026 Sony Corporation
 */

import {
  RealtimeEventSchema,
  type ClientFrame,
  type RealtimeEvent,
} from '@ips-device-verse/twin-service/schema/realtime';

export type RealtimeStatus = 'connecting' | 'live' | 'offline';

export interface RealtimeClientOptions {
  /** `wss://…`, from the deployed stack's config. */
  url: string;
  /** The world whose events and agent replies this socket carries. */
  worldId: string;
  getIdToken: () => Promise<string | undefined>;
  onEvent: (event: RealtimeEvent) => void;
  onStatus: (status: RealtimeStatus) => void;
  /** Injected in tests; the browser's own by default. */
  createSocket?: (url: string) => WebSocket;
  schedule?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
}

export interface RealtimeClient {
  /** False when there is no open socket; the caller decides whether that is an error. */
  send: (frame: ClientFrame) => boolean;
  close: () => void;
}

const FIRST_RETRY_MS = 500;
const MAX_RETRY_MS = 30_000;

/**
 * The browser's end of the push channel.
 *
 * Reconnects with backoff, because the socket will drop: API Gateway closes an
 * idle connection at two hours, laptops sleep, and proxies are proxies. A fresh
 * token is fetched on every attempt rather than reused, so a reconnect after an
 * hour asleep does not fail on an expired one.
 *
 * Every frame is parsed against the shared schema. A socket is the one input
 * that arrives without a request, and treating it as trusted because it came
 * from our own API would make the browser's state depend on the service never
 * being wrong.
 */
export const createRealtimeClient = ({
  url,
  worldId,
  getIdToken,
  onEvent,
  onStatus,
  createSocket = (target) => new WebSocket(target),
  schedule = setTimeout,
}: RealtimeClientOptions): RealtimeClient => {
  let socket: WebSocket | undefined;
  let closed = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const retryDelay = () => Math.min(FIRST_RETRY_MS * 2 ** attempt, MAX_RETRY_MS);

  const connect = async () => {
    if (closed) {
      return;
    }
    onStatus('connecting');

    const token = await getIdToken();
    if (closed) {
      return;
    }
    if (!token) {
      // Signed out, or the refresh failed. Retrying is right: the session may
      // come back, and the alternative is a tab that never reconnects.
      onStatus('offline');
      timer = schedule(() => void connect(), retryDelay());
      attempt += 1;
      return;
    }

    const target = `${url}?world_id=${encodeURIComponent(worldId)}&token=${encodeURIComponent(token)}`;
    const next = createSocket(target);
    socket = next;

    next.onopen = () => {
      attempt = 0;
      onStatus('live');
    };

    next.onmessage = (message: MessageEvent<string>) => {
      const parsed = RealtimeEventSchema.safeParse(JSON.parse(message.data) as unknown);
      if (parsed.success) {
        onEvent(parsed.data);
      }
    };

    next.onclose = () => {
      if (closed || socket !== next) {
        return;
      }
      onStatus('offline');
      timer = schedule(() => void connect(), retryDelay());
      attempt += 1;
    };

    // `onerror` is always followed by `onclose`, so reconnecting is handled in
    // one place rather than racing two.
    next.onerror = () => undefined;
  };

  void connect();

  return {
    send: (frame) => {
      if (socket?.readyState !== WebSocket.OPEN) {
        return false;
      }
      socket.send(JSON.stringify(frame));
      return true;
    },

    close: () => {
      closed = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      socket?.close();
      socket = undefined;
    },
  };
};
